// src/components/sat/AccommodationBar.tsx
'use client'

// The accommodation controls for the PRACTICE surfaces (#830 follow-on):
// text-size (with the large-print badge) + read-aloud, in one strip.
//
// The full-length runner has its own toolbar with these controls placed among
// Directions / Calculator / Reference. Practice screens have no such toolbar,
// which is why reader and large print never reached them. Rather than paste the
// pair into four screens, each screen mounts this and passes its content root.
//
// Renders NOTHING when the user has no relevant accommodation and no zoom
// preference — practice stays visually unchanged for the ~80-85% of students
// who don't need this, while remaining one hook away for those who do.

import ReadAloud from '@/components/sat/ReadAloud'
import TextZoom from '@/components/sat/TextZoom'
import type { AccommodationFlags } from '@/lib/sat/accommodations'

export default function AccommodationBar({
  accommodations,
  zoomLevel,
  onZoomChange,
  readingRootRef,
  questionKey,
  className,
}: {
  accommodations: AccommodationFlags
  zoomLevel: number
  onZoomChange: (level: number) => void
  /** The element wrapping the question content — what read-aloud reads. */
  readingRootRef: React.RefObject<HTMLElement | null>
  /**
   * Current question id. Keys <ReadAloud> so speech stops and the read-along
   * highlight clears when the student advances — otherwise it keeps reading the
   * previous question's text into the new one.
   */
  questionKey: string
  className?: string
}) {
  // Show the zoom control whenever the student is zoomed in (they may have set
  // it on a mock test) or has large print; show read-aloud only for reader.
  const showZoom = accommodations.largePrint || zoomLevel !== 1
  if (!showZoom && !accommodations.reader) return null

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className ?? ''}`}
      role="group"
      aria-label="Accessibility controls"
    >
      {showZoom && (
        <TextZoom
          level={zoomLevel}
          onChange={onZoomChange}
          largePrint={accommodations.largePrint}
        />
      )}
      {accommodations.reader && <ReadAloud key={questionKey} rootRef={readingRootRef} reader />}
    </div>
  )
}
